
import { useState, useCallback, useEffect } from 'react';
import { APIError, useAxios } from '../../core/_axios';
import { Ipad, Service } from '../../core/_models';
import { removeServiceFromIpadByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getIPads, sendServiceToIpadByUniqueIdentifier } from '../../core/_requests_v2';
import { Link } from 'react-router-dom';
import { Tablet, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import useIpadDeviceChannel from '../../hooks/useIpadDeviceChannel';
import { useAuth } from '../../AuthContext';
import useAccount from '../../hooks/useAccount';
import { isPaymentOutOfDate, getAgencyName } from '../../utils/paymentUtils';

// Component for individual iPad row with presence information
function IpadRowWithPresence({ 
    ipad, 
    service, 
    onSendService, 
    onRemoveService, 
    sendingService 
}: { 
    ipad: Ipad, 
    service: Service, 
    onSendService: (ipad: Ipad) => void, 
    onRemoveService: (ipad: Ipad) => void,
    sendingService: boolean 
}) {
    const { ipadIsConnected } = useIpadDeviceChannel({ iPad: ipad });
    
    return (
        <div className="flex items-center justify-between gap-5 lg:gap-7.5 py-2.5 lg:py-3.5 border-b border-gray-200">
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                    {ipadIsConnected ? (
                        <div className="flex items-center gap-1" title="iPad is online">
                            <Wifi className="w-4 h-4 text-green-500" />
                            <span className="flex w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1" title="iPad is offline">
                            <WifiOff className="w-4 h-4 text-gray-400" />
                            <span className="flex w-2 h-2 bg-gray-300 rounded-full"></span>
                        </div>
                    )}
                </div>
                <div>
                    <h4 className="text-lg font-medium">
                        {ipad.public_data.ipad_name}
                    </h4>
                    <p className="text-sm text-gray-500">
                        {ipadIsConnected ? 'Connected' : 'Offline'}
                    </p>
                </div>
            </div>
            <div>
                {ipad.active_service?.unique_identifier !== service.unique_identifier ? (
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onSendService(ipad)}
                        disabled={sendingService || !ipadIsConnected}
                        title={!ipadIsConnected ? 'iPad must be online to send service' : ''}
                    >
                        Send Service
                    </button>
                ) : (
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => onRemoveService(ipad)}
                        disabled={sendingService}
                    >
                        Remove Service
                    </button>
                )}
            </div>
        </div>
    );
}

export default function SendServiceToIpad({
    service,
}: {
    service: Service,
}) {
    const axios = useAxios();
    const { currentUser, viewingAllAccounts } = useAuth();
    const { account } = useAccount({
        axios,
        isForCurrentAccount: !viewingAllAccounts
    });

    usePageTitle('Send Service to iPad');

    const [sendingService, setSendingService] = useState(false);
    const [errorSendingService, setErrorSendingService] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [loadingiPads, setLoadingiPads] = useState(true);
    const [loadingIpadsError, setLoadingIpadsError] = useState<string | null>(null);
    const [iPads, setiPads] = useState<Ipad[]>([]);

    const onSendServiceToIpad = useCallback(async (iPad: Ipad) => {
        setSendingService(true);
        setErrorSendingService(null);
        setSuccessMessage(null);
        try {
            const resultingiPad = await sendServiceToIpadByUniqueIdentifier(axios, iPad.unique_identifier, service.unique_identifier);
            setiPads((prevIpads) => {
                return prevIpads.map((ipad) => {
                    if (ipad.unique_identifier === resultingiPad.unique_identifier) {
                        return resultingiPad;
                    }
                    return ipad;
                });
            });
            setSuccessMessage(`Service successfully sent to ${iPad.public_data.ipad_name}`);
            setSendingService(false);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorSendingService(errorMessage)
            setSendingService(false)
        }
    }, [axios, service.unique_identifier]);

    const onRemoveServiceFromIpad = useCallback(async (iPad: Ipad) => {
        setSendingService(true);
        setErrorSendingService(null);
        setSuccessMessage(null);
        try {
            const resultingiPad = await removeServiceFromIpadByUniqueIdentifier(axios, iPad.unique_identifier);
            setiPads((prevIpads) => {
                return prevIpads.map((ipad) => {
                    if (ipad.unique_identifier === resultingiPad.unique_identifier) {
                        return resultingiPad;
                    }
                    return ipad;
                });
            });
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorSendingService(errorMessage)
            setSendingService(false)
        }
    }, [axios]);

    const loadiPads = useCallback(async () => {
        setLoadingiPads(true);
        setLoadingIpadsError(null);
        try {
            const {
                ipads: updatediPads
            } = await getIPads(axios, service.unique_identifier);
            setiPads(updatediPads);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingIpadsError(errorMessage)
        } finally {
            setLoadingiPads(false)
        }
    }, [axios, service.unique_identifier]);

    useEffect(() => {
        loadiPads();
    }, [loadiPads]);

    if (loadingiPads) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingIpadsError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingIpadsError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadiPads}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Block access if payment is out of date
    const paymentOutOfDate = isPaymentOutOfDate(currentUser, account);

    if (paymentOutOfDate) {
        const agencyName = getAgencyName(currentUser, account);
        return (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
                <div className="col-span-2">
                    <div className="flex flex-col gap-5 lg:gap-7.5">
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">
                                    Send Service to iPad
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <i className="ki-filled ki-information-circle text-6xl text-red-600 mb-4"></i>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                                        Feature Restricted
                                    </h3>
                                    <p className="text-gray-600 mb-4 max-w-md">
                                        Sending services to iPads is currently restricted because payment for {agencyName} is past due.
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Please contact your agency administrator or SGB support to resolve this issue.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Handle the case when no iPads are available
    if (iPads.length === 0) {
        return (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
                <div className="col-span-2">
                    <div className="flex flex-col gap-5 lg:gap-7.5">
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">
                                    Send Service to iPad
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="text-center py-8">
                                    <div className="flex justify-center mb-4">
                                        <div className="p-3 bg-warning-light rounded-full">
                                            <AlertCircle className="w-8 h-8 text-warning" />
                                        </div>
                                    </div>
                                    <h4 className="text-xl font-semibold mb-2">No iPads Available</h4>
                                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                        You need to set up at least one iPad before you can send services to it. 
                                        iPads are used to collect signatures at your funeral home.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                        <Link to="/add-ipad" className="btn btn-primary">
                                            <Tablet className="w-4 h-4 mr-2" />
                                            Add Your First iPad
                                        </Link>
                                        <Link to={`/services/${service.unique_identifier}`} className="btn btn-light">
                                            View Service Details
                                        </Link>
                                    </div>
                                </div>
                                <Link to={`/services/${service.unique_identifier}`} className="mt-4 p-4 bg-info-light rounded-lg block hover:bg-info-light/80 transition-colors">
                                    <p className="text-sm text-info-dark">
                                        <strong>Tip:</strong> You can also collect signatures using QR codes without an iPad. 
                                        Click here to view the service and generate a QR code.
                                    </p>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            Send Service to iPad
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        {successMessage && (
                            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                                <span className="block sm:inline">{successMessage}</span>
                            </div>
                        )}
                        {errorSendingService && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                                <span className="block sm:inline">{errorSendingService}</span>
                            </div>
                        )}
                        <div>
                            <p>
                                From the list below, select the iPad you would like to send this service to:
                            </p>
                        </div>
                        <div>
                            {iPads.map((ipad) => (
                                <IpadRowWithPresence
                                    key={ipad.unique_identifier}
                                    ipad={ipad}
                                    service={service}
                                    onSendService={onSendServiceToIpad}
                                    onRemoveService={onRemoveServiceFromIpad}
                                    sendingService={sendingService}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}